// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import type { CeilingPlane, ModelEditOp } from '../../api/types';
import { kindOf } from '../../app/keyTargets';
import { useEditorCursor, useEditorGesture, useGeometriEditor, type GeometriEditor, type SnapToggles } from '../../app/bygning/GeometriEditorContext';
import { useJobs } from '../../app/JobsContext';
import { useAsync } from '../../app/useAsync';
import { modeTabByPath } from '../../bygning/modes';
import {
  IMPORT_SOURCES,
  defaultRedigerTab,
  importHistory,
  parseImportSource,
  redigerHref,
  type ImportSource,
  type RedigerTab,
} from '../../bygning/rediger';
import {
  elementBox,
  elementStorey,
  elementTree,
  findElement,
  geometryWarnings,
  type GeoWarning,
  type GeometriModel,
} from '../../bygning/geometri';
import { clickSelect } from '../../bygning/geometriSelect';
import { applicableCommands } from '../../bygning/modifyTool';
import { deleteOps, editLabel, elementTitle, fieldOps, lengthInput, propertiesView, resetFieldOps, revertOps, type FieldKey } from '../../bygning/geometriEdit';
import { CODE_WAIT_MS, flushKeys, resolveKeys } from '../../bygning/geometriKeys';
import { saveState } from '../../bygning/geometriSync';
import type { Selection } from '../../bygning/selection';
import { formatDateTime } from '../../deling/model';
import { Checkbox } from '../../components/Checkbox';
import { ElementTree } from '../../components/bygning/rediger/ElementTree';
import {
  MeasureOptionsStrip,
  ModifyStrip,
  OpeningOptionsStrip,
  RoomOptionsStrip,
  RoomPreviewBanner,
  SlabOptionsStrip,
  ZoneOptionsStrip,
  SelectionBar,
  StairOptionsStrip,
  StatusLine,
  StoreyChip,
  ToolDock,
  ViewCluster,
  WallOptionsStrip,
  type AnnotationsStatus,
} from '../../components/bygning/rediger/GeometriChrome';
import { dimGeometry, guideLabel, onStorey } from '../../bygning/cadMeasure';
import { annotationCount, annotationTitle } from '../../bygning/annotationEdit';
import { PropertiesPanel, type CeilingActions, type ZoneActions } from '../../components/bygning/rediger/PropertiesPanel';
import { StoreyPanel, type StoreyActions } from '../../components/bygning/rediger/StoreyPanel';
import { addPlaneOps, ceilingView, fitOps, fitSummary, fitWarning, flatCeilingOps, removePlaneOps, ringCentre, updatePlaneOps } from '../../bygning/ceilingEdit';
import { defaultSection } from '../../bygning/section';
import { isSlabRow, removeHoleOps } from '../../bygning/slabEdit';
import {
  addStoreyOps,
  deleteStoreyOps,
  groundOps,
  groundStoreyId,
  mergeStoreyOps,
  reassignAllOps,
  reassignOps,
  splitStoreyOps,
  storeyDeleteRefusal,
  storeyHeightOps,
  storeyNameOps,
  storeyRows,
} from '../../bygning/storeyEdit';
import { SnitPanel } from './SnitPanel';
import { StairProfile } from '../../components/bygning/rediger/StairProfile';
import { defaultRisers, stairRise } from '../../bygning/stairs';
import { nextRoomName } from '../../bygning/roomTool';
import { createZoneOps, deleteZoneOps, nextZoneName, roomZoneView, setRoomZoneOps, zoneFieldOps, zoneLabel, zoneRows, zoneSwatch, type ZoneResult } from '../../bygning/zones';
import { useSettings } from '../../components/bygning/settings/SettingsContext';
import type { ModelSettingsState, SettingsAction } from '../../components/bygning/settings/settingsState';
import { EmptyState } from '../../components/EmptyState';
import { Kbd } from '../../components/Kbd';
import { Pill } from '../../components/Pill';
import { Spinner } from '../../components/Spinner';
import { BimImportDialog } from '../../components/bygning/BimImportDialog';
import { RtabmapImportDialog } from '../../components/posegraf/RtabmapImportDialog';
import { GraphViewPage } from '../GraphViewPage';
import type { ModeComponent, ModeProps, ModeView } from './modeTypes';
import styles from './RedigerMode.module.css';

/** A count the server may not know (an older ruxd without the route): null. */
function countOrNull<T>(run: (signal: AbortSignal) => Promise<T[]>): (signal: AbortSignal) => Promise<number | null> {
  return async (signal) => {
    try {
      return (await run(signal)).length;
    } catch (cause) {
      if (signal.aborted) throw cause;
      return null;
    }
  };
}

/**
 * `/rediger` with no sub-tab: picks Geometri, Scan & pose or Import from
 * what the case holds (`defaultRedigerTab`) and replaces the URL with it,
 * keeping the query.
 */
function useDefaultTab(active: boolean): void {
  const navigate = useNavigate();
  const { search } = useLocation();
  const walls = useAsync(countOrNull((s) => (active ? api.walls(s) : Promise.resolve([]))), [active]);
  const spaces = useAsync(countOrNull((s) => (active ? api.spaces(s) : Promise.resolve([]))), [active]);
  const frames = useAsync(
    async (signal) => {
      if (!active) return 0;
      try {
        return (await api.projectSummary(signal)).sensor_frames.total_count;
      } catch (cause) {
        if (signal.aborted) throw cause;
        return null;
      }
    },
    [active],
  );
  const tab = defaultRedigerTab({ walls: walls.data, spaces: spaces.data, sensorFrames: frames.data });
  useEffect(() => {
    if (active && tab) navigate(`${redigerHref(tab)}${search}`, { replace: true });
  }, [active, tab, navigate, search]);
}

/**
 * Rediger (frontend-u0o.1): the editing mode, for editors and owners — the
 * tab is hidden from a viewer, whose deep link lands read-only (the shell's
 * Læseadgang notice; every write control checks `useCanEdit()` itself).
 * Three sub-tabs, nested routes the shell draws as a bar under its top bar,
 * in the order the work goes:
 *
 *  - Import (`/rediger/import?source=`): every source that writes geometry
 *    — RTAB-Map sessions, Speckle, IFC — as one page: the sources left, the
 *    chosen source's form in the centre, the import history right.
 *  - Scan & pose (`/rediger/scan`): the pose-graph and scan-repair editor
 *    (the old Posegraf, `/graph-view`), laid over the Ground as a cover —
 *    it has its own expert canvas.
 *  - Geometri (`/rediger/geometri`): the building model (walls, openings,
 *    spaces, roof) edited over the Ground's plan or 3D model as in a CAD
 *    program (frontend-u0o.9; `useGeometriView`).
 */
export const RedigerMode: ModeComponent = ({ data, sel, setSel }) => {
  const location = useLocation();
  const tab = (modeTabByPath(location.pathname)?.tab ?? null) as RedigerTab | null;
  useDefaultTab(tab === null);
  const { state: settings, dispatch } = useSettings();
  const geometri = useGeometriView({ sel, setSel, settings, dispatch });

  if (tab === 'scan') return { cover: <GraphViewPage /> };
  if (tab === 'import') return importView(parseImportSource(location.search), data.canEdit);
  if (tab === 'geometri') return geometri;
  return {
    centre: (
      <div className={styles.waiting}>
        <Spinner label="Åbner Rediger…" />
      </div>
    ),
  };
};

// ------------------------------------------------------------ geometri --

/** The history label of a field edit: "Tykkelse · Væg 6". */
const FIELD_LABEL: Record<FieldKey, string> = {
  length: 'Længde',
  thickness: 'Tykkelse',
  height: 'Højde',
  base: 'Underkant',
  external: 'Type',
  width: 'Bredde',
  opening_height: 'Højde',
  sill: 'Brystning',
  opening_kind: 'Art',
  swing_side: 'Slagretning',
  hinge: 'Hængsel',
  leaves: 'Fløje',
  type_name: 'Type',
  name: 'Navn',
  space_kind: 'Type',
  room_label: 'Scanningsrum',
  floor: 'Gulv',
  ceiling_height: 'Loftshøjde',
  position: 'Placering',
  area: 'Areal',
  holes: 'Huller',
  slab_kind: 'Art',
  slab_top: 'Overkant',
  stair_going: 'Grund',
  stair_risers: 'Trin',
  stair_width: 'Bredde',
  stair_location: 'Ganglinje',
  stair_to: 'Til etage',
  stair_cut: 'Hul i dæk',
  stair_riser: 'Stigning',
  stair_run: 'Løbslængde',
  stair_comfort: '2s + g',
  stair_from: 'Fra etage',
};

/** How many history rows the browser shows before "Vis alle". */
const HISTORY_ROWS = 5;

interface GeometriProps {
  sel: Selection;
  setSel: ModeProps['setSel'];
  settings: ModelSettingsState;
  dispatch: (action: SettingsAction) => void;
}

/**
 * Rediger › Geometri as a CAD editor (frontend-u0o.9, design.md §3): the
 * plan (or 3D) is the page; around it only what the moment needs — the
 * browser left (Etager with their elements, Historik), the selection bar
 * top centre and Egenskaber right while something is selected, the view
 * cluster top right, the tool dock bottom centre and the status line along
 * the bottom; the storey chip sits in the sub-tab bar. The model, the
 * selection and the writes are the shared editor's
 * (`GeometriEditorContext`); this lays them out and takes the keys.
 */
function useGeometriView({ sel, setSel, settings, dispatch }: GeometriProps): ModeView {
  const editor = useGeometriEditor();
  const model = editor?.model ?? EMPTY_MODEL;
  const selection = editor?.selection ?? NO_SELECTION;
  const warnings = useMemo(() => geometryWarnings(model), [model]);
  const tree = useMemo(() => elementTree(model, { uncertainOnly: sel.uncertain, warnings }), [model, sel.uncertain, warnings]);
  const storeyFilter = settings.buildingElements.storey;
  const [allHistory, setAllHistory] = useState(false);
  const [applying, setApplying] = useState(false);
  // A ceiling fit waiting for Anvend: its planes in Snit (S7).
  const [ceilingPreview, setCeilingPreviewState] = useState<{ guid: string; planes: CeilingPlane[] } | null>(null);
  const setCeilingPreview = useCallback((p: { guid: string; planes: CeilingPlane[] } | null) => setCeilingPreviewState(p), []);

  const storeyOf = useCallback(
    (guid: string) => {
      const found = findElement(model, guid);
      return found ? elementStorey(model, found) : null;
    },
    [model],
  );
  const shownWarnings = useMemo(
    () => (storeyFilter.storeyId === null ? warnings : warnings.filter((w) => w.guids.some((g) => storeyOf(g) === storeyFilter.storeyId))),
    [warnings, storeyFilter.storeyId, storeyOf],
  );

  const select = useCallback(
    (guid: string, additive = false) => {
      if (additive && editor) {
        // Shift / ⌘ in the browser toggles it in the selection, as a Shift-click on the plan does.
        editor.select(clickSelect(editor.selection, guid, true), editor.annotationSelection);
        return;
      }
      editor?.select([guid]);
      // A storey shown alone hides the others: follow the element to its own.
      const sid = storeyOf(guid);
      if (storeyFilter.storeyId !== null && sid !== null && sid !== storeyFilter.storeyId) {
        dispatch({ type: 'buildingElementStorey', storey: { ...storeyFilter, storeyId: sid } });
      }
    },
    [editor, storeyOf, storeyFilter, dispatch],
  );
  const zoom = useCallback(
    (guid: string) => {
      const box = elementBox(model, guid);
      if (box) dispatch({ type: 'focus', box });
    },
    [model, dispatch],
  );

  useGeometriKeys(editor);

  // The editor works on one storey's plan: with none picked, open on the
  // ground floor (the storey whose floor is nearest ±0), once per visit.
  const picked = useRef(false);
  const storeyList = model.storeys;
  useEffect(() => {
    if (picked.current || !editor?.active || !storeyList || storeyList.length === 0) return;
    picked.current = true;
    if (storeyFilter.storeyId !== null) return;
    // The ground storey: marked in the Etage editor, else the one nearest ±0.
    const ground = groundStoreyId(storeyList);
    if (ground !== null) dispatch({ type: 'buildingElementStorey', storey: { storeyId: ground, andBelow: false } });
  }, [editor?.active, storeyList, storeyFilter.storeyId, dispatch]);

  const primary = selection[0] ?? null;
  const props = useMemo(() => propertiesView(model, primary), [model, primary]);
  const loading = editor?.loading ?? true;
  const empty = !loading && (model.walls ?? []).length + (model.openings ?? []).length + (model.spaces ?? []).length + (model.slabs ?? []).length === 0;
  const uncertainCount = useMemo(() => elementTree(model, { uncertainOnly: true }).reduce((n, s) => n + s.count, 0), [model]);
  if (!editor) return {};

  const commitField = (key: FieldKey, value: number | string) => {
    if (!primary) return;
    const ops = fieldOps(model, primary, key, value);
    if (ops) void editor.commit(ops, editLabel(FIELD_LABEL[key], model, [primary]));
  };
  const resetField = (key: FieldKey) => {
    if (!primary) return;
    const ops = resetFieldOps(model, primary, key);
    if (ops) void editor.commit(ops, editLabel(`${FIELD_LABEL[key]} tilbage`, model, [primary]));
  };

  const history = editor.history;
  const zones = zoneRows(model, storeyFilter.storeyId);
  const zoneCommit = (r: ZoneResult | null) => {
    if (!r) return;
    if ('error' in r) editor.notify(r.error);
    else void editor.commit(r.ops, r.label);
  };
  const zoneActions: ZoneActions = {
    onMove: (target) => {
      if (!primary) return;
      if (target === 'new') {
        // Out of the zones it is in, into a new one of its own.
        const out = setRoomZoneOps(model, primary, null);
        const made = createZoneOps(model, [primary], editor.newGuid());
        if ('error' in made) return editor.notify(made.error);
        void editor.commit([...(out && 'ops' in out ? out.ops : []), ...made.ops], made.label);
      } else zoneCommit(setRoomZoneOps(model, primary, target));
    },
    onField: (zone, field, value) => zoneCommit(zoneFieldOps(model, zone, field, value)),
    onEdit: (zone) => {
      editor.setTool('zone');
      editor.setZoneTarget(zone);
    },
    onDelete: (zone) => zoneCommit(deleteZoneOps(model, zone)),
  };
  const outcome = (r: { ops: ModelEditOp[]; label: string } | { error: string } | null) => {
    if (!r) return;
    if ('error' in r) editor.notify(r.error);
    else void editor.commit(r.ops, r.label);
  };
  const setStorey = (id: number | null) => dispatch({ type: 'buildingElementStorey', storey: { storeyId: id, andBelow: false } });
  const storeyActions: StoreyActions = {
    onActive: (id) => setStorey(id),
    onRename: (guid, name) => {
      const ops = storeyNameOps(model, guid, name);
      if (ops) void editor.commit(ops, `Omdøb etage · ${name.trim()}`);
    },
    onHeights: (guid, h) => outcome(storeyHeightOps(model, guid, h, editor.storeyCarry)),
    onGround: (guid, on) => {
      const ops = groundOps(model, guid, on);
      const st = model.storeys?.find((x) => x.guid === guid);
      if (ops) void editor.commit(ops, on ? `Stueetage · ${st?.name ?? ''}` : `Ikke stueetage · ${st?.name ?? ''}`);
    },
    onSplit: (guid, z, name) => outcome(splitStoreyOps(model, guid, z, editor.newGuid(), name)),
    onMerge: (guid, other) => {
      const r = mergeStoreyOps(model, guid, other);
      if ('error' in r) return editor.notify(r.error);
      // The merged storey takes the lower idx: the plan follows it.
      const ids = (model.storeys ?? []).filter((x) => x.guid === guid || x.guid === other).map((x) => x.id);
      void editor.commit(r.ops, r.label).then((ok) => ok && setStorey(Math.min(...ids)));
    },
    onDelete: (guid) =>
      new Promise<string | null>((resolve) => {
        const st = model.storeys?.find((x) => x.guid === guid);
        let reason: string | null = null;
        void editor
          .commit(deleteStoreyOps(model, guid), `Slet etage · ${st?.name ?? ''}`, { onError: (m) => (reason = storeyDeleteRefusal(m)) })
          .then((ok) => {
            if (ok && st && storeyFilter.storeyId === st.id) setStorey(groundStoreyId((model.storeys ?? []).filter((x) => x.guid !== guid)));
            resolve(ok ? null : (reason ?? 'Etagen blev ikke slettet'));
          });
      }),
    onMoveAllAndDelete: (guid, toId) => {
      const st = model.storeys?.find((x) => x.guid === guid);
      const ops = reassignAllOps(model, guid, toId, true);
      void editor.commit(ops, `Slet etage · ${st?.name ?? ''}`).then((ok) => ok && setStorey(toId));
    },
    onMoveSelected: (toId) => outcome(reassignOps(model, selection, toId)),
    onAdd: () => {
      const guid = editor.newGuid();
      const r = addStoreyOps(model, guid);
      if ('error' in r) return editor.notify(r.error);
      const rec = r.ops[0] && 'record' in r.ops[0] ? (r.ops[0].record as { idx: number }) : null;
      void editor.commit(r.ops, r.label).then((ok) => ok && rec && setStorey(rec.idx));
    },
    onCarry: (on) => editor.setStoreyCarry(on),
    onSection: () => {
      const line = defaultSection(model, storeyFilter.storeyId);
      if (!line) return editor.notify('Intet at snitte på denne etage');
      editor.setSection(line);
      if (editor.sectionView === 'off') editor.run('view:split');
    },
    onRederive: () => editor.run('rederive'),
    onClose: () => editor.setStoreyPanel(false),
  };
  const ceilingActions = (guid: string): CeilingActions => ({
    onPlane: (i, patch) => outcome(updatePlaneOps(model, guid, i, patch)),
    onAddPlane: () => outcome(addPlaneOps(model, guid)),
    onRemovePlane: (i) => outcome(removePlaneOps(model, guid, i)),
    onFlat: () => outcome(flatCeilingOps(model, guid)),
    onFit: async () => {
      const sp = model.spaces?.find((x) => x.guid === guid);
      try {
        const fit = await api.fitCeiling({ space: guid });
        const summary = fitSummary(fit, sp?.z_floor ?? 0, sp ? ringCentre(sp.polygon.outer) : undefined);
        // The preview is seen where it can be judged: in a section across the room, over the scan.
        if (fit.ok && editor.sectionView === 'off') editor.run('section');
        return {
          summary,
          warning: fitWarning(editor.model, guid, fit),
          planes: fit.ok && fit.plane ? [fit.plane] : null,
          apply: fit.ok ? () => outcome(fitOps(editor.model, guid, fit)) : null,
        };
      } catch (cause) {
        return { summary: `Ingen tilpasning: ${cause instanceof Error ? cause.message : 'serveren svarede ikke'}`, warning: null, planes: null, apply: null };
      }
    },
    onPreview: (planes) => setCeilingPreview(planes ? { guid, planes } : null),
    onSection: () => {
      editor.select([guid]);
      editor.run('section');
    },
  });

  const left = (
    <div className={styles.browser}>
      <div className={styles.treeHead}>
        <h2 className={styles.caption}>Etager</h2>
        <Checkbox
          label="Kun usikre"
          hint={uncertainCount.toLocaleString('da-DK')}
          checked={sel.uncertain}
          onChange={(e) => setSel({ uncertain: e.target.checked })}
        />
      </div>
      {loading && tree.length === 0 ? (
        <Spinner label="Indlæser bygningsmodellen…" />
      ) : empty ? (
        <EmptyState
          bare
          title="Ingen bygningsmodel endnu"
          detail="Kør Etager, Vægge og Rum i pipelinen, eller importér en BIM-model."
          action={<Link to={redigerHref('import', { source: 'speckle' })}>Importér BIM-model</Link>}
        />
      ) : tree.length === 0 ? (
        <p className={styles.note}>Ingen usikre elementer.</p>
      ) : (
        <ElementTree
          compact
          tree={tree}
          selected={primary}
          selectedAll={selection}
          onSelect={select}
          storey={storeyFilter.storeyId}
          onStorey={(id) => dispatch({ type: 'buildingElementStorey', storey: { storeyId: id, andBelow: false } })}
        />
      )}
      <button type="button" className={styles.storeyEdit} aria-pressed={editor.storeyPanel} onClick={() => editor.run('storeys')} title="Ret etagernes navne, gulv- og lofthøjder, del og flet">
        {editor.storeyPanel ? 'Luk etager' : 'Rediger etager…'} <Kbd>E</Kbd>
      </button>
      {zones.length > 0 && (
        <section className={styles.historySection} aria-labelledby="geo-zones">
          <div className={styles.treeHead}>
            <h2 id="geo-zones" className={styles.caption}>
              Zoner
            </h2>
            <span className={styles.note}>{zones.length.toLocaleString('da-DK')}</span>
          </div>
          <ul className={styles.editList}>
            {zones.map((zr) => {
              const on = editor.tool === 'zone' ? editor.zoneTarget === zr.guid : zr.members.length > 0 && zr.members.every((g) => selection.includes(g));
              return (
                <li key={zr.guid}>
                  <button
                    type="button"
                    className={on ? `${styles.zoneRow} ${styles.zoneRowOn}` : styles.zoneRow}
                    aria-pressed={on}
                    title={zr.use ? `${zr.name} · ${zr.use}` : zr.name}
                    onClick={() => {
                      // Under Zone the row is the zone the clicks edit; else it selects its rooms.
                      if (editor.tool === 'zone') editor.setZoneTarget(zr.guid);
                      else if (zr.members.length > 0) editor.select(zr.members);
                    }}
                  >
                    <i className={styles.zoneSwatch} style={{ background: `var(${zr.swatch})` }} aria-hidden="true" />
                    <span className={styles.zoneText}>
                      <span className={styles.editLabel}>
                        {zr.name}
                        {zr.use && <span className={styles.zoneUse}> · {zr.use}</span>}
                      </span>
                      <span className={styles.zoneDetail}>{zr.detail}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {history !== null && (
        <section className={styles.historySection} aria-labelledby="geo-history">
          <div className={styles.treeHead}>
            <h2 id="geo-history" className={styles.caption}>
              Historik
            </h2>
            {history.length > HISTORY_ROWS && (
              <button type="button" className={styles.linkButton} onClick={() => setAllHistory((a) => !a)}>
                {allHistory ? 'Vis færre' : 'Vis alle'}
              </button>
            )}
          </div>
          {history.length === 0 ? (
            <p className={styles.note}>Ingen rettelser endnu.</p>
          ) : (
            <ol className={styles.editList}>
              {(allHistory ? history : history.slice(0, HISTORY_ROWS)).map((e) => (
                <li key={e.id} className={e.undone ? `${styles.editRow} ${styles.editUndone}` : styles.editRow} title={e.undone ? 'Fortrudt' : undefined}>
                  <span className={styles.editLabel}>{e.label || 'Rettelse'}</span>
                  <time className={styles.editWhen} dateTime={e.created_at}>
                    {relativeTime(e.created_at)}
                  </time>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </div>
  );

  const subjectFound = editor.modify ? findElement(model, editor.modify.guids[0] ?? null) : null;
  const modifySubject = subjectFound ? elementTitle(subjectFound) : null;
  // Guides and chains notesPicked in Vælg (S9) share the bar: Slet takes them with the elements.
  const notesPicked = editor.annotationSelection;
  const elementsTitle = selection.length === 0 ? null : selection.length === 1 ? (props?.title ?? '1 element') : `${selection.length} elementer`;
  // Alone named ("Målkæde 2 · 4,25 m"); beside elements counted, so the bar stays short.
  const notesTitle = notesPicked.length === 0 ? null : selection.length === 0 ? annotationTitle(editor.annotations, notesPicked) : annotationCount(editor.annotations, notesPicked);
  const selectionTitle = [elementsTitle, notesTitle].filter(Boolean).join(' · ');
  const revertable = revertOps(model, selection).length > 0;
  const barCommands = applicableCommands(model, selection);
  const deletable = (editor.canEdit && deleteOps(model, selection).length > 0) || notesPicked.length > 0;
  const storeys = (model.storeys ?? []).map((s) => ({ id: s.id, name: s.name, z: s.z_min })).sort((a, b) => b.id - a.id);
  const underlay = settings.clouds.cloud !== false;
  const planView = settings.planLock;
  const openingTool = editor.tool === 'door' || editor.tool === 'window' || editor.tool === 'opening' ? editor.tool : null;
  // Trappe (S8): where the strip's stair climbs from and to, and a selected stair's side profile.
  const storeyName = (id: number | null) => (id === null ? null : (model.storeys?.find((x) => x.id === id)?.name ?? `Etage ${id}`));
  const stairFrom = storeyName(storeyFilter.storeyId);
  const stairTo = stairRise(model.storeys, storeyFilter.storeyId, editor.stairOptions.toStorey);
  const fromZ = model.storeys?.find((x) => x.id === storeyFilter.storeyId)?.z_min ?? null;
  const stairTargets = [...(model.storeys ?? [])]
    .filter((x) => fromZ !== null && x.z_min > fromZ + 0.05)
    .sort((a, b) => a.z_min - b.z_min)
    .map((x) => ({ id: x.id, name: x.name }));
  const selectedStair = props?.kind === 'stair' ? (model.stairs ?? []).find((t) => t.guid === props.guid) : undefined;
  const stairExtra = selectedStair ? (
    <StairProfile
      stair={selectedStair}
      from={storeyName(selectedStair.storey_id) ?? 'Gulv'}
      to={storeyName(selectedStair.storey_to_id)}
      stale={editor.revision?.solids_stale ?? false}
      rederiving={editor.rederiving}
      canEdit={editor.canEdit}
      onRederive={() => editor.run('rederive')}
    />
  ) : null;

  const overlay = (
    <>
      {editor.roomPreview ? (
        <RoomPreviewBanner
          title={editor.roomPreview.title}
          summary={editor.roomPreview.summary}
          busy={applying}
          onCancel={() => editor.setRoomPreview(null)}
          onApply={() => {
            const p = editor.roomPreview;
            if (!p || applying) return;
            setApplying(true);
            void p.apply().then((ok) => {
              setApplying(false);
              if (ok) editor.setRoomPreview(null);
            });
          }}
        />
      ) : (
        selection.length + notesPicked.length > 0 && (
          <SelectionBar
            title={selectionTitle}
            count={selection.length + notesPicked.length}
            revertable={revertable}
            deletable={deletable}
            annotationsOnly={selection.length === 0}
            commands={barCommands}
            onCommand={editor.run}
          />
        )
      )}
      {editor.sectionView !== 'off' && (
        <SnitPanel
          editor={editor}
          storeyId={primary ? (storeyOf(primary) ?? storeyFilter.storeyId) : storeyFilter.storeyId}
          layout={editor.sectionView}
          besideRight={editor.storeyPanel || (props !== null && editor.tool === 'select' && !editor.modify)}
          preview={ceilingPreview}
        />
      )}
      <ViewCluster
        view={editor.sectionView !== 'off' ? editor.sectionView : planView ? 'plan' : '3d'}
        onView={(v) => editor.run(v === 'plan' ? 'view:plan' : v === '3d' ? 'view:3d' : v === 'snit' ? 'view:snit' : 'view:split')}
        underlay={underlay}
        onUnderlay={() => dispatch({ type: 'cloud', name: 'cloud', on: !underlay })}
        onFit={() => editor.run('zoom-fit')}
      />
      {editor.storeyPanel && (
        <StoreyPanel
          rows={storeyRows(model)}
          activeId={storeyFilter.storeyId}
          selected={selection.length}
          carry={editor.storeyCarry}
          rederiving={editor.rederiving}
          actions={storeyActions}
        />
      )}
      {props && editor.tool === 'select' && !editor.modify && !editor.storeyPanel && (
        <PropertiesPanel
          view={props}
          more={selection.length - 1}
          onField={commitField}
          onReset={resetField}
          onRevert={() => editor.run('revert')}
          onDelete={() => editor.run('delete')}
          onSelect={select}
          onZoom={elementBox(model, props.guid) ? () => zoom(props.guid) : null}
          zone={props.kind === 'space' ? roomZoneView(model, props.guid) : null}
          zoneActions={zoneActions}
          ceiling={props.kind === 'space' ? ceilingView(model, props.guid) : null}
          ceilingActions={ceilingActions(props.guid)}
          holes={(() => {
            const f = props.kind === 'slab' ? findElement(model, props.guid) : null;
            if (!f || f.kind !== 'slab' || !isSlabRow(f.row)) return null;
            return {
              count: f.row.polygon.holes.length,
              onRemove: (i: number) => outcome(removeHoleOps(model, props.guid, i)),
              onDraw: () => {
                editor.setTool('floor');
                editor.setSlabOptions({ mode: 'hole' });
              },
            };
          })()}
          extra={stairExtra}
        />
      )}
      {editor.modify ? (
        <ModifyStrip
          session={editor.modify}
          subject={modifySubject}
          onCopy={(copy) => editor.modify && editor.setModify({ ...editor.modify, copy })}
          onDone={() => editor.setModify(null)}
        />
      ) : (
        <>
          {editor.tool === 'wall' && <WallOptionsStrip options={editor.wallOptions} onChange={editor.setWallOptions} />}
          {editor.tool === 'floor' && <SlabOptionsStrip options={editor.slabOptions} onChange={editor.setSlabOptions} />}
          {editor.tool === 'room' && (
            <RoomOptionsStrip options={editor.roomOptions} nextName={nextRoomName(model.spaces)} onChange={editor.setRoomOptions} onFromWalls={() => editor.run('rooms-from-walls')} />
          )}
          {editor.tool === 'zone' && (
            <ZoneOptionsStrip
              zones={[...(model.zones ?? [])].sort((x, y) => x.id - y.id).map((zz) => ({ guid: zz.guid, name: zoneLabel(zz), swatch: zoneSwatch(zz) }))}
              target={editor.zoneTarget}
              picks={editor.zonePicks.length}
              name={editor.roomOptions.zoneName}
              use={editor.roomOptions.zoneUse}
              nextName={nextZoneName(model.zones)}
              onTarget={(g) => {
                editor.setZoneTarget(g);
                editor.setZonePicks([]);
              }}
              onChange={editor.setRoomOptions}
              onCreate={() => {
                const guid = editor.newGuid();
                const r = createZoneOps(model, editor.zonePicks, guid, editor.roomOptions.zoneName, editor.roomOptions.zoneUse);
                if ('error' in r) return editor.notify(r.error);
                editor.setZonePicks([]);
                editor.setZoneTarget(guid);
                editor.setRoomOptions({ zoneName: '', zoneUse: '' });
                void editor.commit(r.ops, r.label);
              }}
            />
          )}
          {editor.tool === 'stair' && (
            <StairOptionsStrip
              options={editor.stairOptions}
              from={stairFrom}
              targets={stairTargets}
              to={stairTo?.toId ?? null}
              risers={stairTo && stairTo.zTop > stairTo.zBase ? defaultRisers(stairTo.zTop - stairTo.zBase) : null}
              riser={stairTo && stairTo.zTop > stairTo.zBase ? (stairTo.zTop - stairTo.zBase) / defaultRisers(stairTo.zTop - stairTo.zBase) : null}
              onChange={editor.setStairOptions}
            />
          )}
          {openingTool && (
            <OpeningOptionsStrip kind={openingTool} options={editor.openingOptions[openingTool]} onChange={(patch) => editor.setOpeningOptions(openingTool, patch)} />
          )}
          {(editor.tool === 'measure' || editor.tool === 'dimension') && (
            <MeasureOptionsStrip tool={editor.tool} guides={editor.measureOptions.guides} orient={editor.measureOptions.orient} onChange={editor.setMeasureOptions} />
          )}
        </>
      )}
      <ToolDock tool={editor.tool} onTool={editor.setTool} />
      <EditorStatus
        editor={editor}
        storeyId={storeyFilter.storeyId}
        warnings={shownWarnings}
        onPickWarning={(w) => {
          select(w.guids[0]);
          zoom(w.guids[0]);
        }}
      />
    </>
  );
  const subbar = (
    <StoreyChip
      storeys={storeys}
      value={storeyFilter.storeyId}
      onChange={(id) => dispatch({ type: 'buildingElementStorey', storey: { storeyId: id, andBelow: false } })}
    />
  );
  return { left, overlay, subbar };
}

const EMPTY_MODEL: GeometriModel = { storeys: null, walls: null, openings: null, spaces: null, slabs: null };
const NO_SELECTION: readonly string[] = [];

const SNAP_TOGGLES: { key: keyof SnapToggles; label: string; title: string }[] = [
  { key: 'snap', label: 'Snap', title: 'Fang til endepunkter, midtpunkter og kanter (Alt slår det fra under et træk)' },
  { key: 'ortho', label: 'Ortogonal', title: 'Lås til bygningens akser' },
  { key: 'trace', label: 'Planspor', title: 'Fang til de scannede planers spor' },
  { key: 'grid', label: 'Gitter', title: 'Fang til et gitter på 5 mm' },
  {
    key: 'smart',
    label: 'Sporing',
    title: 'Smarte hjælpelinjer: flugt med endepunkter, forlængelser, samme længde og samme afstand (hvil markøren på et punkt for at spore fra det)',
  },
];

/** The status line, reading the cursor itself so a pointer move redraws only it. */
function EditorStatus({
  editor,
  storeyId,
  warnings,
  onPickWarning,
}: {
  editor: GeometriEditor;
  storeyId: number | null;
  warnings: readonly GeoWarning[];
  onPickWarning: (w: GeoWarning) => void;
}) {
  const cursor = useEditorCursor(editor);
  const gesture = useEditorGesture(editor);
  const rev = editor.revision;
  const { annotations, annotate } = editor;
  const annotationStatus = useMemo<AnnotationsStatus>(() => {
    const guides = onStorey(annotations.guides, storeyId).map((g) => ({ id: g.id, label: guideLabel(annotations.guides, g) }));
    const dims = onStorey(annotations.dims, storeyId).map((d) => {
      const g = dimGeometry(d);
      const n = g.stretches.length;
      return { id: d.id, label: `Målkæde ${annotations.dims.indexOf(d) + 1} · ${lengthInput(g.total)} m${n > 1 ? ` (${n} mål)` : ''}` };
    });
    return {
      guides,
      dims,
      always: editor.tool === 'measure' || editor.tool === 'dimension',
      onRemove: (id) => annotate({ type: 'remove', id }),
      onClear: (what) => annotate({ type: 'clear', what, storeyId }),
    };
  }, [annotations, annotate, storeyId, editor.tool]);
  return (
    <StatusLine
      tool={editor.tool}
      wallMode={editor.wallOptions.mode}
      roomMode={editor.roomOptions.mode}
      slabMode={editor.slabOptions.mode}
      selected={editor.selection.length + editor.annotationSelection.length}
      hint={gesture?.hint ?? null}
      readout={gesture?.readout ?? null}
      toggles={SNAP_TOGGLES.map((t) => ({ ...t, on: editor.snap[t.key] }))}
      onToggle={(key) => editor.toggleSnap(key as keyof SnapToggles)}
      cursor={cursor}
      warnings={warnings}
      onPickWarning={onPickWarning}
      saved={saveState(rev?.revision ?? null, editor.pending)}
      canUndo={editor.canUndo}
      canRedo={editor.canRedo}
      onUndo={() => editor.run('undo')}
      onRedo={() => editor.run('redo')}
      stale={rev?.solids_stale ?? false}
      rederiving={editor.rederiving}
      onRederive={() => editor.run('rederive')}
      annotations={annotationStatus}
    />
  );
}

/**
 * The editor's keys while Geometri is shown (`geometriKeys.ts`): tools,
 * the two-letter codes, Del, ⌘Z / ⇧⌘Z, Tab and Esc. Captured ahead of the
 * shell's own keys, so Esc first clears the selection; a field, a dialog
 * and the palette keep theirs.
 */
function useGeometriKeys(editor: GeometriEditor | null): void {
  const live = useRef(editor);
  live.current = editor;
  useEffect(() => {
    if (!editor?.active) return;
    let buffer = '';
    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      timer = null;
      const cmd = flushKeys(buffer);
      buffer = '';
      if (cmd) live.current?.run(cmd);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const ed = live.current;
      if (!ed || e.defaultPrevented) return;
      // A grip drag or a typed dimension owns the keys while it lasts.
      if (ed.gesture.get() !== null) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest('[role="dialog"], [role="menu"]')) return;
      const out = resolveKeys(
        { key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, kind: kindOf(target) },
        buffer,
      );
      if (timer !== null) clearTimeout(timer);
      timer = null;
      buffer = out.buffer;
      if (buffer !== '') timer = setTimeout(flush, CODE_WAIT_MS);
      let consumed = out.consumed;
      for (const cmd of out.commands) {
        // Esc with nothing selected is the shell's (it steps out of the storey focus).
        const nothing = ed.selection.length === 0 && ed.annotationSelection.length === 0;
        if (cmd === 'escape' && nothing && ed.tool === 'select' && !ed.modify && !ed.sectionDraw) continue;
        // Tab with nothing under the last click keeps its focus move.
        if ((cmd === 'cycle' || cmd === 'cycle-back') && nothing) {
          consumed = false;
          continue;
        }
        consumed = true;
        ed.run(cmd);
      }
      if (consumed) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      if (timer !== null) clearTimeout(timer);
    };
  }, [editor?.active]);
}

/** "nu", "4 min", "2 t", "3. nov." */
function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'nu';
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} t`;
  return new Date(t).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
}

// -------------------------------------------------------------- import --

function importView(source: ImportSource, canEdit: boolean): ModeView {
  return {
    leftWithCentre: true,
    left: <ImportSources current={source} />,
    centre: <ImportForms current={source} canEdit={canEdit} />,
    right: <ImportHistory />,
    rightKey: 'import',
  };
}

/** The source list: one link per source (`?source=`). */
function ImportSources({ current }: { current: ImportSource }) {
  const { search } = useLocation();
  return (
    <nav className={styles.sources} aria-label="Importkilder">
      <h2 className={styles.caption}>Kilder</h2>
      <ul className={styles.sourceList}>
        {IMPORT_SOURCES.map((s) => {
          const q = new URLSearchParams(search);
          q.set('source', s.id);
          const on = s.id === current;
          return (
            <li key={s.id}>
              <Link
                className={on ? `${styles.source} ${styles.sourceOn}` : styles.source}
                to={{ search: `?${q.toString()}` }}
                replace
                aria-current={on ? 'page' : undefined}
              >
                <span className={styles.sourceLabel}>{s.label}</span>
                <span className={styles.sourceHint}>{s.hint}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <p className={styles.note}>E57 og PLY kommer senere.</p>
    </nav>
  );
}

/**
 * The three forms, all mounted (the inactive ones hidden), so a source's
 * upload keeps running and its form keeps its state while another source is
 * looked at. They are the old dialogs' bodies, rendered inline.
 */
function ImportForms({ current, canEdit }: { current: ImportSource; canEdit: boolean }) {
  const navigate = useNavigate();
  if (!canEdit) {
    return (
      <div className={styles.form}>
        <EmptyState title="Import kræver redigeringsadgang" detail="Bed sagens ejer om rollen redaktør for at importere scanninger eller BIM-modeller." />
      </div>
    );
  }
  return (
    <div className={styles.form}>
      <div hidden={current !== 'rtabmap'}>
        <RtabmapImportDialog
          inline
          onClose={() => undefined}
          onImported={() => undefined}
          onShow={() => navigate(redigerHref('scan', { tilstand: 'blokke' }))}
        />
      </div>
      <div hidden={current !== 'speckle'}>
        <BimImportDialog inline initialTab="speckle" onClose={() => undefined} />
      </div>
      <div hidden={current !== 'ifc'}>
        <BimImportDialog inline initialTab="ifc" onClose={() => undefined} />
      </div>
    </div>
  );
}

/**
 * Importhistorik: the import jobs the event stream holds (newest first),
 * with what each wrote. The server keeps no import ledger yet, so no undo
 * per import (ruxd-5uv.5).
 */
function ImportHistory() {
  const { jobs } = useJobs();
  const rows = useMemo(() => importHistory(jobs), [jobs]);
  return (
    <section className={styles.history} aria-labelledby="rediger-history">
      <h2 id="rediger-history" className={styles.caption}>
        Importhistorik
      </h2>
      {rows.length === 0 ? (
        <EmptyState bare title="Ingen importer endnu" detail="Importer startet i denne sag vises her, med hvad de skrev." />
      ) : (
        <ol className={styles.historyList}>
          {rows.map((r) => (
            <li key={r.id} className={styles.historyRow}>
              <div className={styles.historyHead}>
                <span className={styles.historySource}>{r.sourceLabel}</span>
                <Pill tone={r.tone}>{r.statusLabel}</Pill>
              </div>
              {r.name && <span className={styles.historyName}>{r.name}</span>}
              {r.written && <span className={styles.historyWritten}>{r.written}</span>}
              <time className={styles.historyWhen} dateTime={r.when}>
                {formatDateTime(r.when)}
              </time>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
